import React from "react";
import currency from "currency.js";
import { Button, ButtonGroup, Dropdown } from "react-bootstrap";
import { UseFieldArrayAppend, UseFieldArrayRemove, UseFieldArrayReplace, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { OrderApi, OrderItemApi } from "../../../../../../../../../../../../services/Api/Organization/types";
import { handleTurnOnBarcodeScanner } from "../../../../../../utils/itemsTableUtils";

interface Props {
	showBarcodeScanner: boolean;
	setShowBarcodeScanner: (value: boolean) => void;
	watchedItems: OrderItemApi[];
	form: UseFormReturn<OrderApi>;
	remove: UseFieldArrayRemove;
	append: UseFieldArrayAppend<OrderItemApi>;
	replace: UseFieldArrayReplace<OrderItemApi>;
	recalculateSummaryTaxes: (
		items: OrderItemApi[],
		taxGross: boolean,
		index?: number,
		val?: number,
		quantity?: string
	) => void;
	fetchProductLastPrice: (item: OrderItemApi, index: number) => void;
}

const OrderFormItemsTableActions = ({
	form,
	remove,
	append,
	replace,
	fetchProductLastPrice,
	showBarcodeScanner,
	setShowBarcodeScanner,
	watchedItems,
	recalculateSummaryTaxes,
}: Props) => {
	const { t } = useTranslation();
	const { getValues, watch, setValue } = form;
	const notEmptyItems = watchedItems.filter((_, index) => index !== watchedItems.length - 1);

	const handleRemoveItemsWithNoQuantity = () => {
		const indexes: number[] = [];
		notEmptyItems.forEach((item, index) => {
			if (!item.quantity) indexes.push(index);
		});
		remove(indexes);

		if (getValues("items").length === 0) {
			append({ position: 0 });
		}
		recalculateSummaryTaxes(watchedItems, watch("tax_gross"));
	};

	const handleRemoveDuplicatedItems = () => {
		const indexes: number[] = [];
		const uniqueItems: OrderItemApi[] = [];
		notEmptyItems.forEach((item, index) => {
			const existingItem = uniqueItems.find((uniqueItem) => uniqueItem?.product?.name === item?.product?.name);
			if (existingItem) indexes.push(index);
			else uniqueItems.push(item);
		});
		remove(indexes);
		recalculateSummaryTaxes(watchedItems, watch("tax_gross"));
	};

	const handleSortItemsAlphabetically = () => {
		if (notEmptyItems.length === 0) return;
		const newItems = [...notEmptyItems];
		const sortedItems = newItems.sort((a, b) => (a.product?.name || "").localeCompare(b.product?.name || ""));
		replace(sortedItems);
	};

	const handleSetLastPrice = () => {
		notEmptyItems.forEach((item, index) => {
			fetchProductLastPrice(item, index);
		});
		recalculateSummaryTaxes(watchedItems, watch("tax_gross"));
	};

	const handleSetReferencePrice = () => {
		notEmptyItems.forEach((item, index) => {
			const productDefaultPrice = item?.product?.default_price_net_money?.amount;
			let price = 0;
			if (productDefaultPrice && item?.volume) {
				price = productDefaultPrice * item.volume;
			}
			setValue(`items.${index}.price_net_money.amount`, price);
			setValue(
				`items.${index}.total_price_net_money.amount`,
				price ? currency(price).multiply(item?.quantity || 0).value : 0
			);
		});
		recalculateSummaryTaxes(watchedItems, watch("tax_gross"));
	};

	return (
		<div>
			<Button
				onClick={() => handleTurnOnBarcodeScanner(showBarcodeScanner, setShowBarcodeScanner)}
				className="me-2"
			>
				{t("common.action.turn_on_scanner")}
			</Button>
			<Dropdown as={ButtonGroup}>
				<Dropdown.Toggle className="btn-light">{t("common.action.more", { ns: "lib" })}</Dropdown.Toggle>
				<Dropdown.Menu>
					<Dropdown.Item onClick={handleRemoveItemsWithNoQuantity}>
						{t("common.action.remove_items_with_no_quantity")}
					</Dropdown.Item>
					<Dropdown.Item onClick={handleRemoveDuplicatedItems}>
						{t("common.action.remove_duplicated_items")}
					</Dropdown.Item>
					<Dropdown.Item onClick={handleSortItemsAlphabetically}>
						{t("common.action.sort_by_names")}
					</Dropdown.Item>
					<Dropdown.Item onClick={handleSetLastPrice}>{t("common.action.set_last_price")}</Dropdown.Item>
					<Dropdown.Item onClick={handleSetReferencePrice}>
						{t("common.action.set_reference_price")}
					</Dropdown.Item>
				</Dropdown.Menu>
			</Dropdown>
		</div>
	);
};

export default OrderFormItemsTableActions;
